import React, { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, WifiOff, X } from 'lucide-react';

export function ReloadPrompt() {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('[PWA] Service Worker registered:', r);
      if (r) {
        // 1시간마다 서비스 워커 업데이트 확인
        setInterval(() => {
          r.update().catch(err => console.warn('[PWA] Periodic update check failed:', err));
        }, 60 * 60 * 1000);

        // 사용자가 다른 탭/창에 갔다가 브라우저 탭으로 복귀했을 때 즉시 갱신 확인
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            r.update().catch(err => console.warn('[PWA] Visibility update check failed:', err));
          }
        });
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Service Worker registration error:', error);
    },
  });

  // autoUpdate 모드: 새 버전 감지 시 1.5초 후 자동 적용 (새로고침)
  useEffect(() => {
    if (needRefresh) {
      const timer = setTimeout(() => {
        updateServiceWorker(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [needRefresh, updateServiceWorker]);

  const close = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  if (!offlineReady && !needRefresh) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] max-w-[360px] p-5 rounded-[2rem] bg-[#050505]/95 border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl text-white flex flex-col gap-4 transition-all duration-300 animate-in fade-in slide-in-from-bottom-5">
      <div className="flex items-start gap-3.5">
        <div className={`p-3 rounded-2xl flex-shrink-0 flex items-center justify-center ${
          needRefresh 
            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-lg shadow-emerald-500/5' 
            : 'bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-lg shadow-blue-500/5'
        }`}>
          {needRefresh ? <RefreshCw className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0 pr-2">
          <h4 className="text-[13px] font-black uppercase tracking-wider text-white flex items-center gap-1.5">
            {needRefresh ? '새로운 버전 배포 완료' : '오프라인 사용 준비 완료'}
          </h4>
          <p className="text-[11px] text-white/50 leading-relaxed font-normal mt-1">
            {needRefresh 
              ? '최신 시장 지표 데이터와 버그 수정사항이 배포되었습니다. 지금 바로 업데이트를 적용하시겠습니까?' 
              : '앱이 로컬 스토리지에 캐싱되어 네트워크 연결이 없어도 언제든 실행 가능합니다.'}
          </p>
        </div>
        <button
          onClick={close}
          className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-all flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex gap-2 justify-end">
        {needRefresh && (
          <button
            onClick={() => updateServiceWorker(true)}
            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-black font-black text-[10px] uppercase tracking-widest rounded-xl transition-all active:scale-[0.98] shadow-lg shadow-emerald-950/20"
          >
            업데이트 적용
          </button>
        )}
        <button
          onClick={close}
          className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white font-bold text-[10px] uppercase tracking-widest rounded-xl transition-all border border-white/5"
        >
          {needRefresh ? '나중에' : '확인'}
        </button>
      </div>
    </div>
  );
}
